import { useToast } from '@chakra-ui/react'
import { useDispatch } from '@missionlabs/api'
import { useFormatToNumberE164 } from '@missionlabs/react'
import { Activity, UserActivity } from '@missionlabs/types'
import isElectron from 'is-electron'
import { ElementType, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { SMSNotification } from 'shared/components/Notifications/message/SMSNotification'
import { VoicemailNotification } from 'shared/components/Notifications/message/VoicemailNotification'
import { WhatsAppNotification } from 'shared/components/Notifications/message/WhatsAppNotification'
import { NotificationProps } from 'shared/components/Notifications/types'
import { useGetUserActivities } from 'shared/hooks/useGetUserActivities'
import { getNameFromActivity } from 'shared/utils/activities'
import { createNotification } from 'shared/utils/notifications/browserNotifications'
import { ElectronNotification } from 'shared/utils/notifications/electronNotifications'

import { useGetUserData } from './useGetUserData'
import { useNotificationRefs } from './useNotificationRefs'

const getPropsForType = (
    type: Activity
): {
    element: ElementType<NotificationProps<any>> | null
    route: string
    title: string
} => {
    if (type === Activity.MESSAGE_RECEIVED) {
        return {
            element: VoicemailNotification,
            route: '/notification/message/voicemail',
            title: 'Voicemail Message Received'
        }
    }
    if (type === Activity.CHAT_MESSAGE_RECEIVED) {
        return {
            element: SMSNotification,
            route: '/notification/message/sms',
            title: 'SMS Message Received'
        }
    }
    if (type === Activity.WHATSAPP_MESSAGE_RECEIVED) {
        return {
            element: WhatsAppNotification,
            route: '/notification/message/whatsapp',
            title: 'WhatsApp Message Received'
        }
    }

    throw new Error('useMessageNotificationListener | Invalid activity type')
}

export const useMessageNotificationListener = (
    isEnabled: boolean,
    browserNotificationsEnabled: boolean
) => {
    const toast = useToast()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { formatToLocalNumber } = useFormatToNumberE164()

    const { newActivities } = useGetUserActivities()

    const { destination } = useGetUserData()

    const messages = useMemo(() => {
        const messageActivityTypes = [
            Activity.MESSAGE_RECEIVED,
            Activity.CHAT_MESSAGE_RECEIVED,
            Activity.WHATSAPP_MESSAGE_RECEIVED
        ]

        const notificationEvents = destination?.notifications?.events

        return newActivities.filter(activity => {
            const isNotificationEnabled = notificationEvents?.includes(activity.activityType)
            return messageActivityTypes.includes(activity.activityType) && isNotificationEnabled
        })
    }, [newActivities, destination])

    const { has: isRecentlyDismissed, add: addRecentlyDismissed } = useNotificationRefs()
    const { has, add, remove, dismissOthers } = useNotificationRefs()

    const presentNotification = useCallback(
        (item: UserActivity, element: ElementType<NotificationProps<any>> | null) => {
            if (!element) return null
            const Element = element
            toast({
                position: 'top-right',
                duration: null,
                render: ({ onClose: closeToast }) => {
                    const { ID } = item

                    add(ID, () => {
                        addRecentlyDismissed(ID)
                        closeToast()
                    })

                    return (
                        <Element
                            item={item}
                            navigate={navigate}
                            dispatch={dispatch}
                            onClose={remove}
                        />
                    )
                }
            })
        },
        [add, addRecentlyDismissed, dispatch, navigate, remove, toast]
    )

    // On mount, set up electron handlers.
    useEffectOnce(() => {
        const actionNotificationHandle = (_: any, type: string, activity: UserActivity) => {
            switch (type) {
                case 'view_message':
                    navigate({ pathname: `/home/feed` }, { state: { activityID: activity.ID } })
                    remove(activity.ID)
                    break
                case 'dismiss':
                    remove(activity.ID)
                    break
                default:
                    break
            }
        }
        ElectronNotification.onAction(actionNotificationHandle)

        return () => {
            ElectronNotification.removeOnAction(actionNotificationHandle)
        }
    })

    // Listen for incoming message activities
    useEffect(() => {
        if (!isEnabled || !messages) return

        // Dismiss all stale notifications (any notification that no longer has an associated message)
        dismissOthers(messages.map(message => message.ID))

        if (messages.length < 1) return

        messages.forEach(item => {
            const { ID, activityType } = item

            const { element, route, title } = getPropsForType(activityType)

            // Ignore notifications already being displayed to prevent spam.
            if (has(ID) || isRecentlyDismissed(ID)) return

            if (!isElectron()) {
                // In-app notification
                presentNotification(item, element)
            } else {
                // Electron API notification
                const handle = ElectronNotification.create(route, item)
                add(ID, async () => {
                    const id = await handle
                    if (!id) return
                    addRecentlyDismissed(ID)
                    ElectronNotification.action(id, 'dismiss', item)
                })
            }

            // Browser API notification
            if (browserNotificationsEnabled) {
                createNotification(
                    title,
                    t('details.from', { value: getNameFromActivity(item, formatToLocalNumber, t) }),
                    { click: window.focus }
                )
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnabled, messages])
}
